import { Droplets, Waves, CircleDot, CloudRain } from "lucide-react";
import { Button } from "./ui/button";
import { useState } from "react";
import { ServicesGrid } from "./ServicesGrid";
import { motion } from "motion/react";
import { useLanguage } from "../context/LanguageContext";

export function IrrigationSolutions() {
  const { t } = useLanguage();
  
  const solutions = [
    {
      icon: Droplets,
      title: "Drip Irrigation Design",
      description: "Precision water delivery system with 90% water efficiency. Custom design for every crop type and farm size.",
      features: ["Water Savings", "Uniform Distribution", "Fertilizer Integration", "Automated Control"]
    },
    {
      icon: Waves,
      title: "Sprinkler Systems",
      description: "Uniform water distribution for large areas. Ideal for field crops, vegetables, and orchards.",
      features: ["Large Coverage", "Adjustable Pressure", "Energy Efficient", "Easy Maintenance"]
    },
    {
      icon: CircleDot,
      title: "Borewell Analysis",
      description: "Scientific water source evaluation, quality testing, and yield optimization for sustainable water usage.",
      features: ["Water Quality Test", "Yield Analysis", "Depth Recommendation", "Sustainability Check"]
    },
    {
      icon: CloudRain,
      title: "Rainwater Harvesting",
      description: "Capture and store rainwater for irrigation. Reduce dependency on groundwater and ensure year-round availability.",
      features: ["Storage Solutions", "Recharge Systems", "Filter Design", "Cost Effective"]
    }
  ];

  return (
    <section id="irrigation-solutions" className="py-32 px-6 lg:px-20 relative overflow-hidden">
      {/* Premium Background with Image Overlay */}
      <div className="absolute inset-0">
        <div 
          className="absolute inset-0 bg-cover bg-center bg-no-repeat"
          style={{ backgroundImage: `url(/src/assets/android-image.avif)` }}
        >
          <div className="absolute inset-0 bg-gradient-to-br from-[#051F20]/95 via-[#0B2B26]/90 to-[#163832]/85"></div>
        </div>
      </div>

      {/* Animated Pattern Overlay */}
      <motion.div 
        className="absolute inset-0 opacity-5"
        style={{
          backgroundImage: `radial-gradient(circle at 25px 25px, #8EB69B 2%, transparent 0%)`,
          backgroundSize: '50px 50px'
        }}
        animate={{
          backgroundPosition: ['0px 0px', '50px 50px']
        }}
        transition={{
          duration: 20,
          repeat: Infinity,
          ease: "linear"
        }}
      />

      <div className="max-w-7xl mx-auto relative z-10">
        {/* Section Header */}
        <div className="text-center mb-20">
          <motion.div 
            initial={{ opacity: 0, scale: 0.8 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            className="inline-block bg-white/10 backdrop-blur-xl px-6 py-3 rounded-full border border-[#8EB69B]/30 mb-6 shadow-2xl"
          >
            <p className="text-[#8EB69B] tracking-wider uppercase text-sm">{t('irrigation.title')}</p>
          </motion.div>
          <motion.h2 
            className="text-5xl lg:text-6xl text-white mb-6 leading-tight"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            {t('irrigation.title')}
          </motion.h2>
          <motion.p 
            className="text-[#DAF1DE]/90 text-lg max-w-3xl mx-auto leading-relaxed"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            {t('irrigation.subtitle')}
          </motion.p>
        </div>

        {/* Solutions Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          {solutions.map((solution: any, index: number) => (
            <motion.div 
              key={index}
              initial={{ opacity: 0, y: 50, rotateX: -20 }}
              whileInView={{ opacity: 1, y: 0, rotateX: 0 }}
              viewport={{ once: true }}
              transition={{ delay: index * 0.15, duration: 0.6 }}
              whileHover={{ 
                y: -15,
                rotateX: 5,
                scale: 1.02
              }}
              style={{ 
                perspective: 1200,
                transformStyle: 'preserve-3d'
              }}
              className="group relative bg-white/10 backdrop-blur-2xl p-10 rounded-3xl border border-white/20 hover:border-[#8EB69B]/50 transition-all duration-500 shadow-2xl"
            >
              {/* 3D Depth Effect */}
              <div 
                className="absolute inset-0 bg-gradient-to-br from-[#8EB69B]/20 via-transparent to-[#235347]/20 rounded-3xl"
                style={{ transform: 'translateZ(-10px)' }}
              />
              
              {/* Glow Effect */}
              <motion.div
                className="absolute -inset-0.5 bg-gradient-to-r from-[#8EB69B] to-[#C9A961] rounded-3xl opacity-0 group-hover:opacity-20 blur-xl"
                animate={{
                  scale: [1, 1.1, 1]
                }}
                transition={{
                  duration: 3,
                  repeat: Infinity,
                  ease: "easeInOut"
                }}
              />

              <div className="flex items-start gap-6 mb-6 relative" style={{ transform: 'translateZ(20px)' }}>
                <motion.div 
                  className="h-16 w-16 bg-gradient-to-br from-[#8EB69B] to-[#235347] rounded-2xl flex items-center justify-center flex-shrink-0 shadow-lg"
                  whileHover={{ scale: 1.1, rotate: 360 }}
                  transition={{ duration: 0.6 }}
                >
                  <solution.icon className="h-8 w-8 text-white" />
                </motion.div>
                <div>
                  <h3 className="text-2xl text-white mb-3 group-hover:text-[#8EB69B] transition-colors">
                    {solution.title}
                  </h3>
                  <p className="text-white/80 leading-relaxed">
                    {solution.description}
                  </p>
                </div>
              </div>
              
              <div className="grid grid-cols-2 gap-3 mb-6 relative" style={{ transform: 'translateZ(15px)' }}>
                {solution.features.map((feature: any, idx: number) => (
                  <motion.div 
                    key={idx}
                    className="bg-[#8EB69B]/10 backdrop-blur-sm px-4 py-2 rounded-xl text-[#DAF1DE] text-sm border border-[#8EB69B]/20"
                    whileHover={{ scale: 1.05, backgroundColor: 'rgba(142, 182, 155, 0.2)' }}
                  >
                    ✓ {feature}
                  </motion.div>
                ))}
              </div>
              

              <Button
                variant="ghost"
                className="text-[#8EB69B] hover:text-[#C9A961] hover:bg-white/10 p-0 h-auto relative"
                style={{ transform: 'translateZ(15px)' }}
                onClick={() => { 
                  // resiliently request quote: set a global flag then dispatch event
                  try { (window as any).__openQuoteRequested = true; } catch {};
                  window.dispatchEvent(new CustomEvent('openQuote'));
                }}
              >
                Get Detailed Quote →
              </Button>
      {/* ServicesGrid stub removed (support page now handles quotes) */}

              {/* Corner Accents */}
              <div className="absolute top-4 right-4 w-16 h-16 border-t-2 border-r-2 border-[#8EB69B]/20 group-hover:border-[#8EB69B]/60 rounded-tr-3xl transition-colors duration-300"></div>
            </motion.div>
          ))}
        </div>
      </div>

      {/* Decorative Elements */}
      <motion.div 
        className="absolute bottom-0 left-0 w-[500px] h-[500px] bg-[#8EB69B]/10 rounded-full blur-3xl"
        animate={{
          scale: [1, 1.3, 1],
          opacity: [0.1, 0.2, 0.1]
        }}
        transition={{
          duration: 8,
          repeat: Infinity,
          ease: "easeInOut"
        }}
      />
    </section>
  );
}